import { PanelOrientation } from '../types';
import { SETTING_DEFAULTS, SETTING_TYPES, MM_TO_INCH } from '../constants';
import { SystemKey } from '../constants/aurora.constants';

// BOM Multipliers
const END_CLAMPS_PER_ROW = 4;
const MID_CLAMPS_MULTIPLIER = 2;
const RAILS_MULTIPLIER = 2;
const SPLICE_KITS_MULTIPLIER = 2;

const DEFAULT_SYSTEM_KEY: SystemKey = 'system_one';

export interface PanelRow {
  id: string;
  orientation: PanelOrientation;
  panel_count: number;
  system_key?: SystemKey;
}

export interface ModuleDimensions {
  width: number;
  height: number;
}

export interface BOMCalculationInput {
  structures: Array<{
    panel_rows_1?: { rows: PanelRow[] };
    panel_rows_2?: { rows: PanelRow[] };
    rail_1?: { length?: number };
    rail_2?: { length?: number };
  }>;
  moduleDimensions: Partial<Record<SystemKey, ModuleDimensions>>;
  safetyFactor?: number;
}

export interface BOMCalculationResult {
  totalRails: number;
  totalSpliceKits: number;
  totalMidClamps: number;
  totalEndClamps: number;
  totalGroundingLugs: number;
  /** Raw sums before rounding — used by LGCY to apply offset on unrounded values */
  rawTotalRails: number;
  rawTotalSpliceKits: number;
}

interface RowWithRail {
  row: PanelRow;
  railLength: number;
}

export const mmToInch = (mm: number): number => {
  return mm / MM_TO_INCH;
};

const getAllPanelRows = (input: BOMCalculationInput): PanelRow[] => {
  return input.structures.flatMap(structure => [
    ...(structure.panel_rows_1?.rows || []),
    ...(structure.panel_rows_2?.rows || [])
  ]);
};

const getAllRowsWithRails = (input: BOMCalculationInput): RowWithRail[] => {
  const result: RowWithRail[] = [];

  for (const structure of input.structures) {
    if (structure.panel_rows_1?.rows && structure.rail_1?.length) {
      for (const row of structure.panel_rows_1.rows) {
        result.push({ row, railLength: structure.rail_1.length });
      }
    }
    if (structure.panel_rows_2?.rows && structure.rail_2?.length) {
      for (const row of structure.panel_rows_2.rows) {
        result.push({ row, railLength: structure.rail_2.length });
      }
    }
  }

  return result;
};

const countRowsWithPanels = (allRows: PanelRow[]): number => {
  return allRows.filter(row => row.panel_count > 0).length;
};

/** Resolves module dimensions for a panel row based on its system_key */
const getRowDimensions = (
  row: PanelRow,
  moduleDimensions: Partial<Record<SystemKey, ModuleDimensions>>,
): ModuleDimensions => {
  const dims = moduleDimensions[row.system_key ?? DEFAULT_SYSTEM_KEY];
  return dims ?? { width: 0, height: 0 };
};

/**
 * Base ratio: (panel_count * panel_dimension_inches) / rail_length_inches
 * Module dimensions are in mm (from Aurora), converted to inches.
 * Rail length is stored in inches in the DB.
 */
const calculateBaseRatio = (
  row: PanelRow,
  railLengthInches: number,
  moduleDimensions: Partial<Record<SystemKey, ModuleDimensions>>,
): number => {
  if (row.panel_count <= 0 || railLengthInches <= 0) return 0;

  const { width, height } = getRowDimensions(row, moduleDimensions);
  const widthInches = mmToInch(width);
  const heightInches = mmToInch(height);
  const dimensionInches = row.orientation === PanelOrientation.LANDSCAPE ? heightInches : widthInches;

  return (row.panel_count * dimensionInches) / railLengthInches;
};

/**
 * Total Rails per Excel formula:
 *   AI = panel_count * dimension
 *   AJ = AI / rail_length
 *   AK = AJ * 2
 *   Standard: ROUNDUP(SUM(AK), 0) + safety_factor
 *   LGCY:     ROUNDUP(SUM(AK) * 1.05, 0)  — handled by applyOffset in lgcy-bom.utils
 */
const calculateTotalRails = (input: BOMCalculationInput): { value: number; raw: number } => {
  const safetyFactor = input.safetyFactor ?? SETTING_DEFAULTS[SETTING_TYPES.SAFETY_FACTOR];

  const raw = getAllRowsWithRails(input).reduce((sum, { row, railLength }) => {
    return sum + calculateBaseRatio(row, railLength, input.moduleDimensions) * RAILS_MULTIPLIER;
  }, 0);

  return { value: Math.ceil(raw) + safetyFactor, raw };
};

/**
 * Splice Kits per Excel formula:
 *   AI = panel_count * dimension
 *   AJ = AI / rail_length
 *   AM = IF(panel_count=1, 0, FLOOR(AJ) * 2)
 *   Standard: ROUNDUP(SUM(AM), 0) + safety_factor
 *   LGCY:     ROUNDUP(SUM(AM) * 1.05, 0)  — handled by applyOffset in lgcy-bom.utils
 */
const calculateTotalSpliceKits = (input: BOMCalculationInput): { value: number; raw: number } => {
  const safetyFactor = input.safetyFactor ?? SETTING_DEFAULTS[SETTING_TYPES.SAFETY_FACTOR];

  const raw = getAllRowsWithRails(input).reduce((sum, { row, railLength }) => {
    if (row.panel_count <= 1) return sum;
    const ratio = calculateBaseRatio(row, railLength, input.moduleDimensions);
    return sum + Math.floor(ratio) * SPLICE_KITS_MULTIPLIER;
  }, 0);

  return { value: Math.max(2, Math.ceil(raw) + safetyFactor), raw };
};

/**
 * Total Mid Clamps = SUM(max(panel_count - 1, 0) * 2)
 */
const calculateMidClamps = (allRows: PanelRow[]): number => {
  return allRows.reduce((total, row) => {
    return total + Math.max(row.panel_count - 1, 0) * MID_CLAMPS_MULTIPLIER;
  }, 0);
};

/**
 * Total End Clamps = rows_with_panels * 4
 */
const calculateEndClamps = (allRows: PanelRow[]): number => {
  return countRowsWithPanels(allRows) * END_CLAMPS_PER_ROW;
};

/**
 * Total Grounding Lugs = rows_with_panels
 */
const calculateGroundingLugs = (allRows: PanelRow[]): number => {
  return countRowsWithPanels(allRows);
};

export const calculateBOM = (input: BOMCalculationInput): BOMCalculationResult => {
  const allRows = getAllPanelRows(input);

  const rails = calculateTotalRails(input);
  const spliceKits = calculateTotalSpliceKits(input);

  return {
    totalRails: rails.value,
    totalSpliceKits: spliceKits.value,
    totalMidClamps: calculateMidClamps(allRows),
    totalEndClamps: calculateEndClamps(allRows),
    totalGroundingLugs: calculateGroundingLugs(allRows),
    rawTotalRails: rails.raw,
    rawTotalSpliceKits: spliceKits.raw,
  };
};
